import { useEffect, useState } from "react";
import { useAuth } from "@/components/providers/AuthProvider";

interface LoginCardProps {
  error?: string | null;
}

export function LoginCard({ error }: LoginCardProps) {
  const { signInWithGoogle, signInWithApple } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsSigningIn(false);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const handleGoogleSignIn = async () => {
    setIsSigningIn(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      console.error("Google sign in error:", err);
      setIsSigningIn(false);
    }
  };

  const handleAppleSignIn = async () => {
    setIsSigningIn(true);
    try {
      await signInWithApple();
    } catch (err) {
      console.error("Apple sign in error:", err);
      setIsSigningIn(false);
    }
  };

  return (
    <div className="flex flex-col items-start gap-4">
      {/* Logomark */}
      <img
        src="/images/logomarkLarge.png"
        alt="TinyClub"
        width={88}
        height={88}
      />

      {/* Content section */}
      <div className="flex flex-col items-start gap-5 w-full">
        {/* Title + subtitle */}
        <div className="flex flex-col items-start gap-2">
          <h1 className="text-h4 text-text-primary">Welcome to TinyClub</h1>
          <p className="text-body-2 text-text-secondary">
            Sign in or sign up below.
          </p>
        </div>

        {/* Error banner */}
        {error && (
          <div className="w-full p-4 rounded-2xl bg-accent-negative/10 border border-accent-negative/20 text-accent-negative text-body-3 text-center">
            {error === "auth_failed"
              ? "Sign in failed. Please try again."
              : error}
          </div>
        )}

        {/* OAuth buttons */}
        <div className="w-full flex flex-col gap-3">
          <button
            onClick={handleAppleSignIn}
            disabled={isSigningIn}
            className="w-full flex items-center justify-center gap-[4px] h-[41px] rounded-full bg-white border border-[#bfbfbf] hover:bg-surface-1 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <AppleIcon />
            <span className="font-semibold text-[16px] text-black" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' }}>
              Continue with Apple
            </span>
          </button>

          <button
            onClick={handleGoogleSignIn}
            disabled={isSigningIn}
            className="w-full flex items-center justify-center gap-[13px] h-[41px] rounded-full bg-white border border-[#ccc] hover:bg-surface-1 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <GoogleIcon />
            <span className="font-medium text-[17px] text-black">
              Continue with Google
            </span>
          </button>
        </div>

        {/* Footer */}
        <div className="text-body-3 text-text-tertiary">
          <a
            href="mailto:support@tiny.club"
            className="font-medium hover:text-text-secondary transition-colors"
          >
            Contact support
          </a>
          <span>{" | "}</span>
          <a
            href="https://tiny.club/download"
            className="font-medium hover:text-text-secondary transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download mobile app
          </a>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="black">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}
